import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import { useState } from "react";
import { MultiSelect } from "../ui/multi-select";
import { Filter, X } from "lucide-react";

interface FilterSheetProps {
  onFiltersChange: (filters: FilterValues) => void;
}

export interface FilterValues {
  countries: string[];
  industries: string[];
  sources: string[];
}

const countries = [
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Bolivia",
  "Brazil",
  "Canada",
  "Chad",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Croatia",
  "Denmark",
  "Ecuador",
  "El Salvador",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Guadeloupe",
  "Hong Kong",
  "India",
  "Ireland",
  "Israel",
  "Italy",
  "Japan",
  "Latvia",
  "Mexico",
  "Netherlands",
  "Norway",
  "Panama",
  "Peru",
  "Poland",
  "Portugal",
  "Puerto Rico",
  "Qatar",
  "Serbia",
  "Singapore",
  "South Africa",
  "Spain",
  "Switzerland",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Virgin Islands (British)",
];

const industries = [
  "AI",
  "AR",
  "Advertising",
  "Agritech",
  "Charities",
  "Data",
  "Design",
  "E-commerce",
  "Education",
  "Energy",
  "Entertainment",
  "Event management",
  "Fintech",
  "Gaming",
  "HR",
  "Hardware",
  "Healthtech",
  "IT",
  "Industrials",
  "IoT",
  "Legal",
  "Lifestyle",
  "Mobility",
  "NGOs",
  "Politics",
  "Proptech",
  "SaaS",
  "Security",
  "Social media",
  "Sports",
  "Sustainability",
  "Telecommunications",
  "Travel",
  "VR",
  "Venture capital",
  "Web3",
  "analytics",
  "cleantech",
  "consumer goods",
  "content",
  "fashion",
  "financial services",
  "fitness",
  "foodtech",
  "government",
  "hospitality",
  "international trade",
  "logistics",
  "machine learning",
  "manufacturing",
  "marketing",
  "media",
  "networking",
  "private equity",
  "professional services",
  "real estate",
  "recruitment",
  "retail",
  "robotics",
  "transportation",
  "utilities",
  "wellness",
];

const sources = ["Web Summit Vancouver 2026", "Web Summit Vancouver 2025", "Web Summit Rio 2025"];

export function FilterSheet({ onFiltersChange }: FilterSheetProps) {
  const [filters, setFilters] = useState<FilterValues>({
    countries: [],
    industries: [],
    sources: [],
  });

  const handleFilterChange = (newFilters: Partial<FilterValues>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const hasActiveFilters =
    filters.countries.length > 0 ||
    filters.industries.length > 0 ||
    filters.sources.length > 0;

  const clearFilters = () => {
    const emptyFilters: FilterValues = {
      countries: [],
      industries: [],
      sources: [],
    };
    setFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[400px] p-4 sm:p-6">
        <SheetHeader>
          <SheetTitle>Filter Companies</SheetTitle>
        </SheetHeader>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2 text-muted-foreground mt-2"
          >
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}

        <div
          className="flex flex-col gap-6 py-4"
          onClick={(e) => e.stopPropagation()}
        >
          <ScrollArea className="h-[calc(100vh-140px)] pr-4">
            <div className="space-y-4">
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <Label className="text-base">Countries</Label>
                <MultiSelect
                  id="countries-filter"
                  options={countries.map((country) => ({
                    label: country,
                    value: country,
                  }))}
                  onValueChange={(values) =>
                    handleFilterChange({ countries: values })
                  }
                  value={filters.countries}
                  placeholder="Select countries..."
                  modalPopover={true}
                  showSearch={true}
                />
              </div>

              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <Label className="text-base">Industries</Label>
                <MultiSelect
                  id="industries-filter"
                  options={industries.map((industry) => ({
                    label: industry,
                    value: industry,
                  }))}
                  onValueChange={(values) =>
                    handleFilterChange({ industries: values })
                  }
                  value={filters.industries}
                  placeholder="Select industries..."
                  modalPopover={true}
                  showSearch={true}
                />
              </div>

              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <Label className="text-base">Source</Label>
                <MultiSelect
                  id="sources-filter"
                  options={sources.map((source) => ({
                    label: source,
                    value: source,
                  }))}
                  onValueChange={(values) =>
                    handleFilterChange({ sources: values })
                  }
                  value={filters.sources}
                  placeholder="Select sources..."
                  modalPopover={true}
                  showSearch={true}
                />
              </div>

            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}
